import ActionPerformerHelper from "@/utils/moreMenu/ActionPerfomerHelper.ts";
import GenericValues from "@/utils/common/GenericValues.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import MetaData from "@/utils/common/MetaData.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import GenericValue = namaTypes.GenericValue;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import FieldMetaData = namaTypes.FieldMetaData;
import FieldInformation = namaTypes.FieldInformation;
import ListOfGenericValues = namaTypes.ListOfGenericValues;
import EntityReferenceData = namaTypes.EntityReferenceData;

export default class ListViewPerformerHelper extends ActionPerformerHelper
{
  private readonly presenter: ListPresenter;
  externalContextFields: EntityReferenceData & { namaRowIndex: any };

  constructor(presenter: ListPresenter) {
    super();
    this.presenter = presenter;
  }

  override fetchPresenter(): WidgetsPresenter {
    return this.presenter;
  }

  get place() {
    return this.presenter.fetchPlace();
  }

  get gridApi() {
    return this.presenter.gridApi.value!;
  }

  get metadata() {
    return this.presenter.fetchMetadata();
  }

  createSearchContextInfo(fieldId: string, lineNumber?: number): FieldActionContextInfo {
    if (!lineNumber || lineNumber !== 0)
      lineNumber = -1;
    let fieldMetadata: FieldMetaData | undefined = MetaData.findFieldMetadata(fieldId, this.metadata);
    let fieldInfo: FieldInformation | undefined;
    if (fieldMetadata)
      fieldInfo = fieldMetadata.fieldInformation;
    let hasSearcherContext: boolean | undefined = fieldInfo && fieldInfo.hasSearchContext;
    let searchContextInfo: FieldActionContextInfo = {};
    searchContextInfo.souceLineLinumber = lineNumber;
    searchContextInfo.sourceFieldId = fieldId;
    searchContextInfo.sourceEntityType = this.getEntityType();
    searchContextInfo.contextFields = {};
    searchContextInfo.sourcFieldParentId = MetaData.getFieldParentDetail(this.metadata, fieldId);
    let searchContextFields: string[] = hasSearcherContext && fieldInfo && fieldInfo.searchContextFields ? fieldInfo.searchContextFields : [];
    searchContextFields = [...searchContextFields]; //copy fields to add dynamic fields
    if (fieldInfo && fieldInfo.dynamicSearchContextFields && fieldInfo.dynamicSearchContextFields.length)
      searchContextFields.push(...fieldInfo.dynamicSearchContextFields);
    return searchContextInfo;
  }

  override addContextFieldsToAction(contextFieldIds: string[], contextRow: number, contextFields: Record<string, GenericValue>): void {
    let selectedRowIds: string[];
    let listViewPageIds: string[];
    if (ObjectChecker.isNotEmptyOrNull(this.externalContextFields)) {
      for (let [key, value] of Object.entries(this.externalContextFields)) {
        contextFields[key] = GenericValues.createGenericValueForType(key, value);
      }
      const rowId = this.externalContextFields[CommonFieldIds.ID];
      listViewPageIds = selectedRowIds = [rowId]
      contextFields[CommonFieldIds.ID] = GenericValues.createGenericValue(CommonFieldIds.ID, this.metadata, rowId, contextRow);
    }
    if (contextFieldIds.contains(CommonFieldIds.LIST_VIEW_SELECTED_IDS) || ObjectChecker.isNotEmptyOrNull(selectedRowIds)) {
      if (ObjectChecker.isEmptyOrNull(this.externalContextFields))
        selectedRowIds = AGGridUtils.fetchSelectedRows(this.gridApi).map(l => l.id);
      contextFields[CommonFieldIds.LIST_VIEW_SELECTED_IDS] = GenericValues.createGenericValueForList(
        CommonFieldIds.LIST_VIEW_SELECTED_IDS,
        selectedRowIds,
        "java.lang.String");
    }
    if (contextFieldIds.contains(CommonFieldIds.LIST_VIEW_PAGE_IDS) || ObjectChecker.isNotEmptyOrNull(listViewPageIds)) {
      if (ObjectChecker.isEmptyOrNull(this.externalContextFields))
        listViewPageIds = AGGridUtils.getCurrentPageData(this.gridApi).map(l => l.id);
      contextFields[CommonFieldIds.LIST_VIEW_PAGE_IDS] = GenericValues.createGenericValueForList(
        CommonFieldIds.LIST_VIEW_PAGE_IDS,
        listViewPageIds,
        "java.lang.String");
    }
    if (contextFieldIds.contains(CommonFieldIds.LIST_VIEW_CRITERIA)) {
      contextFields[CommonFieldIds.LIST_VIEW_CRITERIA] = GenericValues.createGenericValueForObject(CommonFieldIds.LIST_VIEW_CRITERIA,
        /*TODO:: Add Criteria*/'',
        undefined,
        "com.namasoft.common.criteria.DTOCriteria");
    }
    for (let field of contextFieldIds) {
      if (ObjectChecker.areNotEqual(field, CommonFieldIds.LIST_VIEW_SELECTED_IDS) && field.startsWith(CommonFieldIds.LIST_VIEW_SELECTED_IDS)) {
        let selectedRows: EntityReferenceData[] = [];
        if (ObjectChecker.isNotEmptyOrNull(this.gridApi))
          selectedRows = AGGridUtils.fetchSelectedRows(this.gridApi);
        else
          selectedRows = [this.externalContextFields];
        this.addField(field.substring(CommonFieldIds.LIST_VIEW_SELECTED_IDS.length), field, contextFields, selectedRows);
      } else if (ObjectChecker.areNotEqual(field, CommonFieldIds.LIST_VIEW_PAGE_IDS) && field
        .startsWith(CommonFieldIds.LIST_VIEW_PAGE_IDS)) {
        let fieldId = field.substring(CommonFieldIds.LIST_VIEW_PAGE_IDS.length);
        let values = ListViewPerformerHelper.getFieldValues(fieldId, AGGridUtils.getCurrentPageData(this.gridApi));
        this.addField(field.substring(CommonFieldIds.LIST_VIEW_PAGE_IDS.length), field, contextFields, values);
      }
    }
  }

  private addField(fieldId: string, contextFieldId: string, contextFields: Record<string, GenericValue>, selectedRows: any[]) {
    let values = ListViewPerformerHelper.getFieldValues(fieldId, selectedRows);
    let displayedField = this.presenter.layout?.displayColumns?.find(l => l.id === fieldId);
    if (!displayedField)
      return;
    let fieldType = displayedField.metaData?.fieldType;
    if (fieldType && fieldType === FieldType.Genericreference) {
      let oldIds = values;
      values = [];
      for (let id of oldIds) {
        values.push({
          valuesMap: id
        });
      }
      contextFields[contextFieldId] = GenericValues.createGenericValueForList(contextFieldId, values, "com.namasoft.common.flatobjects.FlatObject");
    } else
      contextFields[contextFieldId] = GenericValues.list(contextFieldId, values);
  }

  override getEntityType(): string {
    return this.place.entityType!;
  }

  override listViewName(): string | undefined {
    return this.place.listViewName;
  }

  private static getFieldValues(fieldId: string, rows: Record<string, any>[]) {
    let ids: any[] = [];
    for (let row of rows) {
      ids.push(row[fieldId]);
    }
    return ids;
  }

  override entityMetadata(): namaTypes.NaMaMetaData {
    return this.metadata;
  }

  override beforeGoToPlace() {
    // TODO
    // if (getHistoryURLUpdater() != null)
    //   getHistoryURLUpdater().beforeGoToPlace();
  }

  override async refresh(doNotHideErrors: boolean, doNotHideWarnings: boolean) {
    this.gridApi.refreshServerSide({
      purge: true
    })
  }

  override updateRelatedFields(result: ListOfGenericValues, row: number): boolean {
    return false;
  };

  override getSearchAndSuggestionsProvider(): BaseDispatcher | null {
    return null;
  };

  validate(fieldId: string, row?: number) {
  }
}
